import { useEffect, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { AlertCircle, Gift } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Spinner } from '@/components/ui/Spinner';
import { effektiveLieferadresse, fehlendeVersandfelder } from '@/lib/adresse';
import { heuteIso } from '@/lib/format';
import type { Bestellung } from '@/types/detail';
import { faengtAussenklickAb } from '@/onboarding/tourSchutz';
import { useZeigerFein } from '@/hooks/useLayout';
import { NachlieferungKopieren } from './NachlieferungKopieren';
import { melde } from '@/lib/nutzung';

type Wahl = 'sendung' | 'warten' | 'ohne_sendung';

type Props = {
  open: boolean;
  /**
   * Fasst dieser Dialog den Zustand an? (A7.4 / #50)
   *
   * `true`  — der Weg ueber „Alle eingetroffen — bereit setzen": Sendung
   *           anlegen UND Zustand auf `bereit`. So war es immer.
   * `false` — der Weg ueber „Sendung erstellen" an der Sendungs-Sektion:
   *           nur die Sendung, der Zustand bleibt, wo er ist.
   *
   * Der Befund dahinter: Um fuer eine teilweise eingetroffene Bestellung
   * eine Teilsendung anzulegen, musste man sie erst auf „Bereit" setzen —
   * obwohl sie nicht bereit war. Der Weg zur Teilsendung fuehrte ueber
   * einen Zustand, der die Unwahrheit sagt.
   *
   * Bewusst DERSELBE Dialog und nicht ein zweiter daneben: die Regeln,
   * welche Positionen in eine Sendung duerfen (B23/B24), stehen sonst an
   * zwei Orten und laufen auseinander.
   */
  mitZustand?: boolean;
  bestellung: Bestellung;
  versandarten: { id: number; code: string; label: string }[];
  isPending?: boolean;
  /** Serverfehler; solange gesetzt, bleibt der Dialog mit den Eingaben offen (B18). */
  fehler?: string | null;
  onConfirm: (
    modus: 'vollstaendig' | 'teil_eingetroffen' | null,
    versandartId: number | null,
    /** „Als Geschenk verpacken" — sitzt an der Sendung, nicht an der
        Position: ein Paket wird verpackt, nicht ein Buch daraus. */
    istGeschenk: boolean,
    /** Versanddatum (A46). Bei Abholung null. */
    versandDatum: string | null,
    /** Sendungsnummer, freiwillig (A46). Bei Abholung null. */
    trackingNr: string | null
  ) => void;
  /** Bewusst kein Zustandswechsel — nur schliessen und bestaetigen (B19). */
  onWarten: () => void;
  onCancel: () => void;
};

export function SendungDialog({
  open,
  mitZustand = true,
  bestellung,
  versandarten,
  isPending = false,
  fehler = null,
  onConfirm,
  onWarten,
  onCancel: onCancelRoh
}: Props) {
  // Nutzungsmessung: wie oft wird der Sendungs-Dialog ohne Ergebnis
  // geschlossen?
  const onCancel = (): void => {
    melde('dialog_abgebrochen', { ort: mitZustand ? 'sendung_bereit' : 'sendung_anlegen' });
    onCancelRoh();
  };
  // Bereits in Sendungen verplante Mengen je Position (B24).
  const verplant = new Map<number, number>();
  for (const s of bestellung.sendungen) {
    for (const sp of s.positionen) {
      verplant.set(sp.position_id, (verplant.get(sp.position_id) ?? 0) + sp.menge);
    }
  }

  const offenVon = (id: number, menge: number): number =>
    Math.max(0, menge - (verplant.get(id) ?? 0));

  const total = bestellung.positionen.length;
  // Eingetroffen heisst vollstaendig eingetroffen, nicht "irgendetwas da" (B23).
  // Gegen die wirksame Menge (Migration 035): eine ganz vergriffene
  // Position gilt als komplett — sie haelt nichts auf und liefert nichts.
  const komplett = bestellung.positionen.filter((p) => p.menge_eingegangen >= p.menge_wirksam);
  const alleKomplett = total > 0 && komplett.length === total;
  // Was koennte diese Sendung ueberhaupt aufnehmen?
  const versandfaehig = komplett.filter((p) => offenVon(p.id, p.menge_wirksam) > 0);
  const nichtsOffen = bestellung.positionen.every((p) => offenVon(p.id, p.menge_wirksam) === 0);

  const sendungMoeglich = versandfaehig.length > 0;

  // E52 — was nach dieser Teilsendung nachgeliefert wird: alles, was
  // wirksam bestellt, aber weder vollständig da noch schon verplant ist.
  // Genau jetzt entsteht die erste Rechnung, deshalb steht der Satz hier.
  const zeigerFein = useZeigerFein();
  const kommtNach = bestellung.positionen.filter(
    (p) => p.menge_wirksam > 0 && !komplett.includes(p) && offenVon(p.id, p.menge_wirksam) > 0
  );

  /**
   * A56.4 (#135) — DIE EINE WARNUNG, und nur diese.
   *
   * Steht die Bestellung auf „Erst wenn alles da ist" und deckt die
   * Sendung nicht alle Positionen ab, laeuft hier etwas gegen den
   * Wunsch der Kundin. Genau dieser Augenblick ist der einzige, in dem
   * die Regel gebrochen wuerde — und der einzige Ort, an dem eine
   * Warnung ohne neue Mechanik auskommt.
   *
   * EIN SATZ, KEIN RIEGEL: Nur warnen, nie sperren. Der Knopf bleibt
   * benutzbar; die praktische Ausnahme („hat zu lange gedauert, wir
   * schicken vor und liefern nach") ist Alltag und soll es bleiben.
   *
   * Gerechnet wird auf ALLE Positionen der Bestellung, nicht nur auf
   * die eingetroffenen: „alles da" heisst alles, was bestellt wurde.
   */
  const deckungLuecke = bestellung.positionen.some(
    (p) => offenVon(p.id, p.menge_wirksam) > 0 && p.menge_eingegangen < p.menge_wirksam
  );
  const gegenLieferwunsch =
    bestellung.lieferwunsch === 'komplett' && sendungMoeglich && !alleKomplett && deckungLuecke;

  const [wahl, setWahl] = useState<Wahl>('sendung');
  const [vaId, setVaId] = useState<number | null>(bestellung.versandart?.id ?? null);
  // `=== true` und nicht die blosse Zuweisung: Eine Antwort, die das Feld
  // (noch) nicht traegt, ergaebe sonst `undefined`, und das ginge als
  // „weiss nicht" an den Aufrufer weiter. Die Oberflaeche soll sagen, was
  // sie meint — gefunden von der Zusicherung aus S2-006b.1.
  const [istGeschenk, setIstGeschenk] = useState(bestellung.ist_geschenk === true);
  // Der Hinweis "Versandart fehlt" erscheint erst, wenn jemand wirklich
  // bestaetigen wollte (B57). Vorher ist er eine Ruege fuer etwas, das
  // noch niemand versucht hat.
  const [versuchtBestaetigen, setVersuchtBestaetigen] = useState(false);
  // A46: Eine Versand-Sendung ist mit dem Anlegen draußen. Das Datum
  // steht deshalb HIER und nicht hinter einem zweiten Knopf.
  // `heuteIso()` ist Ortszeit, nicht UTC — sonst stünde abends das Datum
  // von gestern drin (Fallstrick 6).
  const [versandDatum, setVersandDatum] = useState(heuteIso());
  const [trackingNr, setTrackingNr] = useState('');

  // Voreinstellung darf nie auf einer unmoeglichen Option stehen (B18).
  useEffect(() => {
    if (!open) return;
    // Ohne Zustandswechsel sind „warten" und „ohne Sendung" beides
    // Aussagen ueber den Zustand — hier gibt es sie nicht, und die
    // Voreinstellung darf nie auf einer Option stehen, die gar nicht
    // angeboten wird.
    setWahl(sendungMoeglich || !mitZustand ? 'sendung' : 'warten');
    setVaId(bestellung.versandart?.id ?? null);
    // Beim Öffnen auf den WUNSCH der Bestellung stellen (A25).
    //
    // Bis hierher stand hier `false`, aus gutem Grund: Ein Häkchen, das
    // von der vorigen Sendung stehenbleibt, verpackt irgendwann ein
    // Paket, das niemand verpacken wollte. Das gilt weiter — der Wert
    // wird bei jedem Öffnen neu gesetzt, er bleibt nie stehen. Neu ist
    // nur, WORAUF gesetzt wird: auf das, was bei der Aufnahme
    // ausgemacht war, statt auf „nein".
    //
    // Änderbar bleibt es ausdrücklich. Am Packtisch kann sich etwas
    // ergeben haben, was am Telefon niemand wusste; die Sendung trägt
    // dann die Ausführung, und die Bestellung behält ihren Wunsch.
    setIstGeschenk(bestellung.ist_geschenk === true);
    // Beim Öffnen neu auf heute — ein Datum von gestern, das vom letzten
    // Öffnen stehenblieb, wäre eine falsche Aussage über einen Transport.
    setVersandDatum(heuteIso());
    setTrackingNr('');
  }, [open, sendungMoeglich, mitZustand, bestellung.versandart?.id, bestellung.ist_geschenk]);

  const brauchtVersandart = wahl === 'sendung' && bestellung.versandart === null;
  const versandartFehlt = brauchtVersandart && vaId === null;
  const zeigeVersandartWarnung = versandartFehlt && versuchtBestaetigen;

  // Fehlende Anschrift wird GEMELDET, nicht erzwungen (E8).
  //
  // B22 sperrte hier den Bestaetigen-Knopf, und api/sendungen/create.php
  // brach zusaetzlich mit 400 ab. Die Absicht war richtig — eine
  // Zustellung ohne Anschrift ist keine —, der Ort war es nicht:
  // Rechnung und Lieferschein entstehen in BONUS, dort steht die
  // Anschrift. Das Tracking haelt nur den Vorgangszustand fest. Wer die
  // Ware nachweislich weggeschickt hat, muss das eintragen koennen, ohne
  // die Anschrift ein zweites Mal zu pflegen — sonst wird der Versand
  // "spaeter" gebucht und das System sagt die Unwahrheit.
  //
  // Sichtbar bleibt es: der Hinweis unten steht weiterhin da. Abholung
  // war ohnehin ausgenommen.
  const gewaehlteVa =
    bestellung.versandart ?? versandarten.find((v) => v.id === vaId) ?? null;
  const istZustellung = gewaehlteVa !== null && gewaehlteVa.code !== 'abholung';
  // Teil-Abholung (02.11.2026): „Sendung" ist für eine Abholung das
  // falsche Wort, und „ohne Sendung" braucht dort niemand. Übrig bleibt
  // die eine echte Wahl: mitgeben, was da ist — oder warten.
  const teilAbholung = mitZustand && !alleKomplett && gewaehlteVa?.code === 'abholung';
  const fehlendeAdressfelder =
    wahl === 'sendung' && istZustellung
      ? fehlendeVersandfelder(effektiveLieferadresse(bestellung))
      : [];

  // Bei fehlender Versandart bleibt der Knopf absichtlich klickbar: der
  // Klick ist es, der den Hinweis ausloest. Gesperrt wird nur, wo ein
  // Klick nichts bewirken koennte.
  const bestaetigenGesperrt = isPending || (wahl === 'sendung' && !sendungMoeglich);

  function bestaetigen(): void {
    if (versandartFehlt) {
      setVersuchtBestaetigen(true);
      return;
    }
    if (wahl === 'warten') {
      onWarten();
      return;
    }
    if (wahl === 'ohne_sendung') {
      onConfirm(null, null, false, null, null);
      return;
    }
    onConfirm(
      alleKomplett ? 'vollstaendig' : 'teil_eingetroffen',
      vaId,
      istGeschenk,
      // Bei Abholung wird nichts verschickt: kein Datum, keine Nummer.
      istZustellung ? versandDatum : null,
      istZustellung && trackingNr.trim() !== '' ? trackingNr.trim() : null
    );
  }

  const titel = nichtsOffen
    ? 'Alle Positionen sind bereits zugeordnet'
    : !mitZustand
      // Der Titel folgt dem, was WIRKLICH in diese Sendung geht, nicht
      // dem Eingang. Gemessen im laufenden Build: bei zwei eingetroffenen
      // Positionen, von denen eine schon verplant war, stand oben
      // „Sendung erstellen" und darunter „… in diese Teilsendung".
      // `alleKomplett` beantwortet die Frage „ist alles da?", hier zaehlt
      // „geht alles mit?".
      ? versandfaehig.length === total
        ? 'Sendung erstellen'
        : 'Teilsendung erstellen'
      : alleKomplett
        ? 'Sendung anlegen'
        : teilAbholung
          ? 'Abholung vorbereiten'
          : 'Nicht alle Positionen sind eingetroffen';

  return (
    <Dialog.Root open={open} onOpenChange={(o) => !o && onCancel()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-ink/30" />
        <Dialog.Content
          // Die Tour haengt am <body>: ohne diese Zeile gilt ein Klick in
          // die Sprechblase als Klick daneben und schliesst den Dialog.
          onPointerDownOutside={faengtAussenklickAb}
          onInteractOutside={faengtAussenklickAb}
          aria-describedby={undefined}
          data-tour="sendungsdialog"
          className="dialog-mobil-vollbild fixed left-1/2 top-1/2 z-50 w-[min(560px,90vw)] -translate-x-1/2 -translate-y-1/2 rounded-md border border-line bg-cream p-5 shadow-lg"
        >
          <Dialog.Title className="font-serif text-lg text-ink">{titel}</Dialog.Title>

          {nichtsOffen ? (
            <>
              <p className="mt-3 text-sm text-ink-soft">
                Alle Positionen sind bereits Sendungen zugeordnet. Es gibt nichts, was in
                eine neue Sendung könnte.
              </p>
              <div className="mt-5 flex justify-end gap-2">
                <Button variant="ghost" onClick={onCancel}>
                  {mitZustand ? 'Abbrechen' : 'Schließen'}
                </Button>
                {/* „Als bereit markieren" ist eine Aussage ueber den
                    Zustand. Wer hier nur eine Sendung erstellen wollte,
                    bekommt sie nicht angeboten. */}
                {mitZustand && (
                  <Button
                    variant="primary"
                    onClick={() => onConfirm(null, null, false, null, null)}
                    disabled={isPending}
                  >
                    Als bereit markieren
                  </Button>
                )}
              </div>
            </>
          ) : (
            <>
              {/* Ohne Zustandswechsel bleibt genau eine Wahl uebrig:
                  „warten" und „ohne Sendung" sind beides Aussagen ueber
                  den Zustand. Eine Auswahl mit nur einem Punkt ist keine
                  Auswahl — dann steht dort ein Satz statt eines
                  Radioknopfes. */}
              {mitZustand ? (
                <div className="mt-4 space-y-2 text-sm text-ink">
                  <Option
                    checked={wahl === 'sendung'}
                    onCheck={() => setWahl('sendung')}
                    disabled={!sendungMoeglich}
                    label={
                      alleKomplett
                        ? 'Sendung mit allen Positionen erstellen'
                        : teilAbholung
                          ? 'Mitgeben, was da ist'
                          : 'Teilsendung mit eingetroffenen Positionen erstellen'
                    }
                    hint={
                      sendungMoeglich
                        ? `${versandfaehig.length} von ${total} Positionen vollständig eingetroffen`
                        : komplett.length === 0
                          ? 'Noch keine Position vollständig eingetroffen'
                          : 'Alle eingetroffenen Positionen sind bereits verplant'
                    }
                  />
                  <Option
                    checked={wahl === 'warten'}
                    onCheck={() => setWahl('warten')}
                    label={teilAbholung ? 'Warten, bis alles da ist' : 'Auf vollständigen Eingang warten'}
                    hint="Zustand bleibt unverändert."
                  />
                  {!teilAbholung && (
                    <Option
                      checked={wahl === 'ohne_sendung'}
                      onCheck={() => setWahl('ohne_sendung')}
                      label="Trotzdem als bereit markieren ohne Sendung"
                      hint="Selten — nur wenn keine Sendung gewollt."
                    />
                  )}
                </div>
              ) : (
                <p className="mt-4 text-sm text-ink">
                  {sendungMoeglich ? (
                    <>
                      {versandfaehig.length === total
                        ? 'Alle Positionen kommen in diese Sendung.'
                        : `${versandfaehig.length} von ${total} Positionen sind vollständig eingetroffen und kommen in diese Teilsendung.`}
                      <span className="mt-1 block text-xs text-ink-faint">
                        Der Zustand der Bestellung bleibt unverändert.
                      </span>
                    </>
                  ) : (
                    <span className="text-ink-soft">
                      {komplett.length === 0
                        ? 'Noch keine Position ist vollständig eingetroffen. Es gibt nichts, was in eine Sendung könnte.'
                        : 'Alle eingetroffenen Positionen sind bereits einer Sendung zugeordnet.'}
                    </span>
                  )}
                </p>
              )}

              {/* E52: Teilsendung am PC — der Satz für die erste Rechnung.
                  Nicht bei Abholung: dort gibt es kein Porto. */}
              {zeigerFein &&
                sendungMoeglich &&
                kommtNach.length > 0 &&
                gewaehlteVa?.code !== 'abholung' &&
                (!mitZustand || wahl === 'sendung') && (
                  <div className="mt-4">
                    <NachlieferungKopieren titel={kommtNach} />
                  </div>
                )}

              {brauchtVersandart && (
                <div className="mt-4 space-y-2">
                  {/* Hinweis verschwindet, sobald gewaehlt wurde (B20). */}
                  <p className={zeigeVersandartWarnung ? 'text-sm text-coral' : 'text-sm text-ink-soft'}>
                    {zeigeVersandartWarnung ? 'Versandart fehlt. Bitte wählen:' : 'Versandart'}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {versandarten.map((va) => (
                      <button
                        key={va.id}
                        type="button"
                        onClick={() => setVaId(va.id)}
                        className={
                          vaId === va.id
                            ? 'min-h-[44px] rounded-md border border-akzent bg-akzent-sanft px-3 py-1.5 text-sm text-akzent-stark'
                            : 'min-h-[44px] rounded-md border border-line bg-cream px-3 py-1.5 text-sm text-ink-soft hover:border-akzent hover:text-akzent'
                        }
                      >
                        {va.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* A46: Versanddatum und Sendungsnummer, gleich hier.
                  ------------------------------------------------------
                  Bis hierher entstand eine Versand-Sendung ohne Datum,
                  stand auf „Vorbereitet" und wartete auf den Knopf
                  „Versendet markieren" — den zweiten Klick, den der
                  Betreiber nicht macht: „Es passiert manchmal, dass ein
                  Paket liegen bleibt, aber wir werden nicht noch einen
                  Klick machen." Die Bestellung sah danach im Tracking
                  offen aus und war unterwegs.

                  Bei Abholung stehen die beiden Felder NICHT da. Ein
                  Versanddatum an einer Abholung wäre eine Aussage über
                  einen Transport, den es nicht gegeben hat. */}
              {wahl === 'sendung' && sendungMoeglich && istZustellung && (
                <div className="mt-4 flex flex-wrap items-end gap-2">
                  <label className="flex flex-col gap-1 text-xs text-ink-soft">
                    Versanddatum
                    <input
                      type="date"
                      value={versandDatum}
                      onChange={(e) => setVersandDatum(e.target.value)}
                      className="min-h-[44px] rounded-md border border-line bg-cream px-2 py-1 text-sm"
                    />
                  </label>
                  <label className="flex flex-col gap-1 text-xs text-ink-soft">
                    Sendungsnummer (optional)
                    {/* KEIN inputMode="numeric" (CLAUDE.md, Falle 18):
                        UPS beginnt mit „1Z". Ein reines Zifferfeld
                        machte diese Nummern auf iOS uneingebbar. */}
                    <input
                      type="text"
                      value={trackingNr}
                      maxLength={100}
                      onChange={(e) => setTrackingNr(e.target.value)}
                      placeholder="DPD…"
                      className="min-h-[44px] rounded-md border border-line bg-cream px-2 py-1 text-sm"
                    />
                  </label>
                </div>
              )}

              {/* S2-006b.1: „Als Geschenk verpacken".
                  ------------------------------------------------------
                  Die Spalte sendungen.ist_geschenk steht seit dem ersten
                  Schema in der Tabelle, und die Detailansicht liest sie
                  aus — geschrieben hat sie nie jemand. Sie stand im
                  Betrieb immer auf 0, und das Geschenk-Motiv an der
                  Sendungskarte trat nie ein.

                  Das ganze <label> ist die Schaltfläche, nicht nur das
                  Kästchen: 44 Pixel hoch, und ein Tippen irgendwo auf der
                  Zeile schaltet um. Auf dem Tablet ist ein 16-Pixel-
                  Kästchen ein Glücksspiel. */}
              {wahl === 'sendung' && sendungMoeglich && (
                <label className="mt-4 flex min-h-[44px] cursor-pointer items-center gap-2.5 rounded-md border border-line bg-cream px-3 py-2 hover:border-akzent">
                  <input
                    type="checkbox"
                    checked={istGeschenk}
                    onChange={(e) => setIstGeschenk(e.target.checked)}
                    className="h-5 w-5 shrink-0 accent-[color:var(--akzent)]"
                  />
                  <Gift aria-hidden className="h-4 w-4 shrink-0 text-gold-dark" />
                  <span className="text-sm text-ink">Als Geschenk verpacken</span>
                </label>
              )}

              {/* Hinweis, keine Sperre (E8): der Knopf bleibt bedienbar.
                  Die Anschrift führt BONUS — hier steht sie nur, wenn sie
                  ohnehin gepflegt ist. */}
              {fehlendeAdressfelder.length > 0 && (
                <p className="mt-4 flex items-start gap-1.5 text-sm text-gold-dark">
                  <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
                  <span>
                    In der Lieferadresse fehlt {fehlendeAdressfelder.join(', ')}. Das
                    hält den Versand nicht auf — Rechnung und Lieferschein kommen aus
                    BONUS. Ergänzen kannst du sie oben unter „Lieferadresse ändern“.
                  </span>
                </p>
              )}

              {/* A56.4: der Hinweis, kein Riegel. Er steht direkt ueber den
                  Knoepfen, weil dort entschieden wird — und in Gold, nicht
                  in Coral: Es ist kein Fehler, sondern eine Auskunft. */}
              {gegenLieferwunsch && wahl === 'sendung' && (
                <p className="mt-4 flex items-start gap-1.5 text-sm text-gold-dark">
                  <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
                  <span>
                    Die Kundin wollte erst alles zusammen. Es ist noch nicht alles
                    da — wenn du jetzt sendest, geht eine Teillieferung raus. Das
                    hält dich nicht auf.
                  </span>
                </p>
              )}

              {fehler && (
                <p className="mt-4 flex items-start gap-1.5 text-sm text-coral" role="alert">
                  <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
                  <span>{fehler}</span>
                </p>
              )}

              <div className="mt-5 flex justify-end gap-2">
                <Button variant="ghost" onClick={onCancel} disabled={isPending}>
                  Abbrechen
                </Button>
                <Button variant="primary" onClick={bestaetigen} disabled={bestaetigenGesperrt}>
                  {isPending && <Spinner />}
                  Bestätigen
                </Button>
              </div>
            </>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function Option({
  checked,
  onCheck,
  label,
  hint,
  disabled = false
}: {
  checked: boolean;
  onCheck: () => void;
  label: string;
  hint?: string;
  disabled?: boolean;
}) {
  return (
    <label
      className={
        disabled
          ? 'flex cursor-not-allowed items-start gap-2 opacity-60'
          : 'flex cursor-pointer items-start gap-2'
      }
    >
      <input
        type="radio"
        checked={checked}
        onChange={onCheck}
        disabled={disabled}
        className="mt-1 accent-akzent"
      />
      <span>
        <span className="block text-ink">{label}</span>
        {hint && <span className="block text-xs text-ink-faint">{hint}</span>}
      </span>
    </label>
  );
}
